<template>
    <vx-modal
        :visible="visible"
        :padding="false"
        size="md"
        @update:visible="emit('update:visible', $event)"
        :stacked="stacked"
        :preventClosing="preventClosing"
    >
        <template v-slot:header>
            <div class="pt-6 px-6 pb-4">Edit Message</div>
        </template>

        <div class="px-12 pb-12 pt-10 flex flex-col gap-8">
            <vx-alert v-if="showSenderAlert" color="info" :closeable="false" class="mb-3">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faDiamondExclamation" />
                </template>
                You are editing a Blast for <strong>{{ props.campaign.sender.name }}</strong>
            </vx-alert>

            <div v-if="loading" class="flex items-center justify-center py-12">
                <vx-icon-loading class="animate-loading-spin text-slate-500 text-2xl" />
            </div>

            <div v-else>
                <vx-label>Message</vx-label>
                <input-with-errors :errors="bodyErrors" :type="errorsType" autoFocus>
                    <template v-slot:alert="{ error }">
                        <quick-blast-alert :error="error" />
                    </template>

                    <message-builder
                        ref="messageBuilder"
                        v-model="message.body"
                        v-model:mediaUrl="message.media_url"
                        v-model:mediaContentType="message.media_content_type"
                        v-model:snippetId="message.snippet_id"
                        v-model:snippetFieldBindings="message.field_bindings"
                        :initialSnippet="initialSnippet"
                        :blockImages="blockImages"
                        :blockFiles="blockFiles"
                        :onlyLockedSnippetsAllowed="onlyLockedSnippetsAllowed"
                        :disableLockedSnippets="false"
                        :blastForecastMode="true"
                        :contextOrgUnitId="props.orgUnitId"
                        preview
                        @selected:snippet="onSnippetSelected"
                    />
                </input-with-errors>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button @click="emit('update:visible', false)" color="muted" size="lg" data-test="cancel-btn"
                    >Cancel</vx-button
                >
                <vx-button
                    @click="updateMessage"
                    color="primary"
                    :loading="saving"
                    :disabled="loading"
                    size="lg"
                    class="grow"
                    data-test="update-btn"
                >
                    Update
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import { VxModal, VxButton, VxAlert, VxLabel, VxIconLoading } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faDiamondExclamation } from '@fortawesome/pro-solid-svg-icons';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import InputWithErrors from '~/components/general/InputWithErrors.vue';
import QuickBlastAlert from '~/components/quick-blast/QuickBlastAlert.vue';
import { campaignsClient } from '~/services';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useMessageBuilderInModal } from '~/composables/useMessageBuilderInModal';
import { useToasted } from '~/composables/useToasted';
import { buildCampaignMessage } from '~/constants/campaign';
import { useValidateMessageStandards } from '~/components/quick-blast/useValidateMessageStandards';
import { catchRequestError } from '~/utils/request';
import { isAxiosError } from 'axios';
import { ref, computed, watch } from 'vue';
import type { BlastForecastCampaignsResponse } from '~/services/blastForecast/blastForecastClient.types';
import type { FieldBinding, Snippet } from '~/types/snippet.types';
import type { MessageRulesResponse } from '~/types/messageRules.types';
import type { ClientContext } from '~/services/contextClient';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    visible: boolean;
    campaign: BlastForecastCampaignsResponse['items'][0];
    orgUnitId?: number;
}>();

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
    (e: 'updated', newMessage: { body: string; media_url: string | null; media_content_type: string | null }): void;
}>();

const { onCorporate } = useCurrentGroup();
const toasted = useToasted();

const message = ref<{
    body: string;
    media_url: string | null;
    media_content_type: string | null;
    snippet_id?: number;
    field_bindings: FieldBinding[];
    status?: string;
}>({
    body: '',
    media_url: null,
    media_content_type: null,
    snippet_id: undefined,
    field_bindings: [],
});

const loading = ref(false);
const saving = ref(false);
const initialSnippet = ref<Snippet | undefined>();
const campaignType = ref<'marketing' | 'transactional' | null>(null);
const currentSnippetIsLocked = ref(false);
const campaignMessageRules = ref<MessageRulesResponse | null>(null);
const campaignGroupIds = ref<string[]>([]);

const campaignLockedSnippetsEnabled = computed(() => {
    if (!campaignMessageRules.value || !campaignType.value || campaignType.value === 'transactional') {
        return false;
    }

    return Boolean(campaignMessageRules.value.marketing_rules?.restrictions?.locked_snippets);
});

const blockImages = computed(() => {
    if (!campaignMessageRules.value || !campaignType.value) {
        return false;
    }
    const rules =
        campaignType.value === 'marketing'
            ? campaignMessageRules.value.marketing_rules
            : campaignMessageRules.value.transactional_rules;

    return Boolean(rules?.restrictions?.block_images);
});

const blockFiles = computed(() => {
    if (!campaignMessageRules.value || !campaignType.value) {
        return false;
    }
    const rules =
        campaignType.value === 'marketing'
            ? campaignMessageRules.value.marketing_rules
            : campaignMessageRules.value.transactional_rules;

    return Boolean(rules?.restrictions?.block_files);
});

const onlyLockedSnippetsAllowed = computed(() => {
    return campaignLockedSnippetsEnabled.value;
});

const campaignAiVettingEnabled = computed(() => {
    if (!campaignMessageRules.value || !campaignType.value) {
        return false;
    }

    const rules =
        campaignType.value === 'marketing'
            ? campaignMessageRules.value.marketing_rules
            : campaignMessageRules.value.transactional_rules;

    return Boolean(rules?.restrictions?.ai_vetting);
});

const showSenderAlert = computed(() => {
    if (props.campaign.sender.type === 'corporate') {
        return false;
    }
    if (onCorporate()) {
        return true;
    }

    return props.campaign.sender.type === 'group';
});

const onSnippetSelected = (snippet: Snippet | null) => {
    currentSnippetIsLocked.value = snippet?.is_locked ?? false;
};

const { stacked, preventClosing } = useMessageBuilderInModal();

const { bodyErrors, validate: validateAi } = useValidateMessageStandards({
    campaignType: () => campaignType.value || 'marketing',
    messageBody: () => message.value.body,
    messageMediaUrl: () => message.value.media_url,
    messageMediaContentType: () => message.value.media_content_type,
    messageSnippetId: () => message.value.snippet_id,
    messageSnippetFieldBindings: () => message.value.field_bindings || [],
    snippetIsLocked: () => currentSnippetIsLocked.value,
    audienceSegmentIds: () => [],
    audienceExternalContactListIds: () => [],
    audienceGroupIds: () => campaignGroupIds.value,
    validationContext: {
        orgUnitId: () => props.orgUnitId,
        aiVettingEnabled: () => campaignAiVettingEnabled.value,
    },
});

const errorsType = computed(() => {
    if (bodyErrors.value.length === 0) {
        return 'danger';
    }
    const hasBlocking = bodyErrors.value.some((e) => e.severity === 'blocking' || !e.severity);
    return hasBlocking ? 'danger' : 'warning';
});

const fetchCampaignDetails = async () => {
    if (props.campaign.sender.type === 'corporate') {
        return;
    }

    const context: ClientContext = onCorporate() ? { teamId } : { teamId, orgUnitId: props.orgUnitId };

    return blastForecastClient.getCampaign(context, props.campaign.id);
};

const fetchCampaignMessageRules = async (): Promise<void> => {
    if (props.orgUnitId) {
        const response = await campaignsClient.getOrgUnitMessageRules(teamId, props.orgUnitId);
        campaignMessageRules.value = response.data;
    } else {
        campaignMessageRules.value = null;
    }
};

watch(
    () => props.visible,
    async (visible) => {
        if (!visible) {
            bodyErrors.value = [];
            campaignMessageRules.value = null;
            campaignGroupIds.value = [];
            return;
        }

        // Corporate campaigns don't use locked snippets - use props data directly
        if (props.campaign.sender.type === 'corporate') {
            message.value = {
                body: props.campaign.main_message.body,
                media_url: props.campaign.main_message.media_url,
                media_content_type: props.campaign.main_message.media_content_type,
                snippet_id: undefined,
                field_bindings: [],
                status: 'published',
            };
            initialSnippet.value = undefined;
            campaignType.value = null;
            currentSnippetIsLocked.value = false;
            campaignMessageRules.value = null;
            campaignGroupIds.value = [];
            return;
        }

        // For OU/Group campaigns - fetch full details to get snippet/field_bindings
        loading.value = true;
        try {
            const [response] = await Promise.all([fetchCampaignDetails(), fetchCampaignMessageRules()]);

            if (!response) {
                return;
            }

            const campaignMessage = response.data.campaign_messages[0];
            if (!campaignMessage) {
                return;
            }

            message.value = {
                body: campaignMessage.body,
                media_url: campaignMessage.media_url,
                media_content_type: campaignMessage.media_content_type,
                snippet_id: campaignMessage.snippet_id ?? undefined,
                field_bindings: campaignMessage.field_bindings ?? [],
                status: campaignMessage.status,
            };

            initialSnippet.value = campaignMessage.snippet ?? undefined;
            campaignType.value = response.data.type;
            currentSnippetIsLocked.value = campaignMessage.snippet?.is_locked ?? false;

            if (props.campaign.sender.type === 'group') {
                campaignGroupIds.value = [String(props.campaign.sender.id)];
            } else if (props.campaign.sender.type === 'org_unit') {
                campaignGroupIds.value = (response.data.groups || []).map((g: { group_id: string }) => g.group_id);
            }
        } catch (error) {
            toasted.error(catchRequestError(error));
            emit('update:visible', false);
        } finally {
            loading.value = false;
        }
    },
    { immediate: true }
);

const updateMessage = async () => {
    if (saving.value || loading.value) {
        return;
    }

    const validationPassed = await validateAi();
    if (!validationPassed) {
        return;
    }

    saving.value = true;
    bodyErrors.value = [];

    const messagePayload = buildCampaignMessage({
        snippetId: message.value.snippet_id,
        snippetIsLocked: currentSnippetIsLocked.value,
        fieldBindings: message.value.field_bindings,
        body: message.value.body,
        mediaUrl: message.value.media_url,
        mediaContentType: message.value.media_content_type,
        status: message.value.status,
    });

    const context: ClientContext = props.orgUnitId ? { teamId, orgUnitId: props.orgUnitId } : { teamId };

    try {
        await blastForecastClient.adjustCampaign(context, props.campaign.id, {
            messages: [messagePayload],
        });

        toasted.success('Message updated');

        emit('updated', {
            body: message.value.body,
            media_url: message.value.media_url,
            media_content_type: message.value.media_content_type,
        });
        emit('update:visible', false);
    } catch (error) {
        handleBackendErrors(error);
    } finally {
        saving.value = false;
    }
};

const handleBackendErrors = (error: unknown) => {
    if (isAxiosError(error)) {
        const errors = error.response?.data?.errors;

        if (errors?.messages) {
            bodyErrors.value = errors.messages.map((msg: string) => ({
                message: msg,
                severity: 'blocking',
            }));
            return;
        }
    }

    toasted.error(catchRequestError(error));
};
</script>
